<script setup>
import { computed } from 'vue'
import { useVModel } from '@vueuse/core'
import { getI18n } from '@/plugins/i18n'

import InfoBox from '@/components/shared/InfoBox.vue'

const i18n = getI18n()

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  message: {
    type: String,
    required: false,
    default: null,
  },
  caption: {
    type: String,
    required: false,
    default: null,
  },
  icon: {
    type: String,
    required: false,
    default: 'mdi-alert',
  },
  iconColor: {
    type: String,
    required: false,
    default: 'warning',
  },
  options: {
    // [{ key, label, icon, color, standalone }] — options flagged `standalone`
    // render as their own button; the rest are grouped in the delete dropdown.
    type: Array,
    required: true,
  },
  deleteLabel: {
    type: String,
    required: false,
    default: null,
  },
  size: {
    type: String,
    required: false,
    default: 'xs',
  },
  loading: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['select', 'cancel', 'update:modelValue'])

const value = useVModel(props, 'modelValue', emit)

// Delete options are grouped in a single dropdown; standalone options (e.g. archive) stay separate.
const deleteChoices = computed(() => props.options.filter(o => !o.standalone))
const standaloneChoices = computed(() => props.options.filter(o => o.standalone))
const dropdownLabel = computed(() => props.deleteLabel || i18n.t('common.delete'))

const onSelect = (key) => {
  emit('select', key)
  value.value = false
}

const onCancel = () => {
  emit('cancel')
  value.value = false
}
</script>
<template>
  <q-dialog
    v-model="value"
    :class="`dialog-${props.size}`"
    persistent
  >
    <q-card>
      <q-card-section class="row items-center no-wrap">
        <q-avatar
          :icon="props.icon"
          :color="props.iconColor"
          text-color="white"
          size="md"
        />
        <div class="text-h6 q-ml-sm">{{ props.title }}</div>
      </q-card-section>
      <q-card-section
        v-if="props.message"
        class="q-pt-none"
      >
        <div>{{ props.message }}</div>
        <info-box
          v-if="props.caption"
          :message="props.caption"
          class="q-mt-sm"
        />
      </q-card-section>
      <q-card-actions align="right">
        <q-btn
          flat
          :label="i18n.t('common.cancel')"
          :disable="props.loading"
          @click="onCancel"
        />
        <q-btn
          v-if="deleteChoices.length === 1"
          flat
          :color="deleteChoices[0].color || 'negative'"
          :icon="deleteChoices[0].icon"
          :label="deleteChoices[0].label"
          :disable="props.loading"
          @click="onSelect(deleteChoices[0].key)"
        />
        <q-btn-dropdown
          v-else-if="deleteChoices.length > 1"
          flat
          color="negative"
          icon="mdi-delete"
          :label="dropdownLabel"
          :disable="props.loading"
          auto-close
        >
          <q-list>
            <q-item
              v-for="option in deleteChoices"
              :key="option.key"
              v-ripple
              clickable
              @click="onSelect(option.key)"
            >
              <q-item-section
                v-if="option.icon"
                avatar
              >
                <q-icon
                  :name="option.icon"
                  :color="option.color || 'negative'"
                />
              </q-item-section>
              <q-item-section>{{ option.label }}</q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
        <q-btn
          v-for="option in standaloneChoices"
          :key="option.key"
          flat
          :color="option.color || 'primary'"
          :icon="option.icon"
          :label="option.label"
          :disable="props.loading"
          @click="onSelect(option.key)"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
